import { toast } from "react-toastify"

import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { mergeMap } from "~/utils/tsUtils"

export const useSaveMetadata = (options?: { showToast?: boolean }) => {
  const utils = api.useUtils()

  return api.product.saveMetadata.useMutation({
    onSuccess: async (data, req) => {
      utils.product.getMetadataForEdit.invalidate()

      // Doing partial update on settings with SKUs which have changed
      utils.variantTableData.settings.setData(undefined, (oldData) => {
        return mergeMap(oldData, data)
      })

      // These fields regenerate ForecastVariant server-side, staling everything derived from it.
      const forecastChanged =
        req.lifecycleStatus !== undefined ||
        req.ignore !== undefined ||
        req.productType !== undefined ||
        req.phasingOutFrom !== undefined ||
        req.activatedFrom !== undefined ||
        req.activatedUntil !== undefined

      const orderParamsChanged =
        req.purchaseIntervalDays !== undefined ||
        req.totalLeadTimeDays !== undefined ||
        req.bufferType !== undefined ||
        req.bufferValue !== undefined

      if (forecastChanged || orderParamsChanged) {
        utils.productTableData.stockOutTimesV2.invalidate()
        utils.stockOutTimes.getStockOutTimesDetails.invalidate()
        utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate()
        utils.stockOutTimes.getSuggestedOrders.invalidate()
        utils.stockOutTimes.getSkuClosingStock.invalidate()
        utils.stockOutTimes.getProjectedStockForPeriods.invalidate()
        // Lead time and buffer are inputs to the walk these three run, not just to the ones above.
        utils.stockOutTimes.getSimulatedSuggestedOrders.invalidate()
        utils.stockOutTimes.getStockLevelsForOrders.invalidate()
        utils.incomingOrders.simulateAvailabilityImpact.invalidate()
        utils.closingStock.getForecastedClosingStockForDays.invalidate()
        utils.closingStock.getForecastedClosingStockForCollections.invalidate()
        utils.purchaseOrder.getAvailabilityViolations.invalidate()
      }

      const orderConstraintsChanged =
        req.minimumOrderQuantity !== undefined ||
        req.secondaryMoq !== undefined ||
        req.colliSize !== undefined

      // Supply plans and POs read MOQ/colli from getDataForPOs; the NOOS MOQ suggestion depends on MOQ.
      if (orderConstraintsChanged) {
        utils.sku.getDataForPOs.invalidate()
        utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate()
        utils.stockOutTimes.getSuggestedOrders.invalidate()
      }

      // Returns depend on lead time (restricted→unrestricted cutoff) and lifecycle (HAK-1795).
      if (
        req.totalLeadTimeDays !== undefined ||
        req.lifecycleStatus !== undefined ||
        req.phasingOutFrom !== undefined
      ) {
        utils.expectedReturns.getForecastedReturnsForVariants.invalidate()
        utils.expectedReturns.getSummedForecastedReturns.invalidate()
        utils.expectedReturns.getForecastedReturnsForCollections.invalidate()
      }

      // Hiding/unhiding a variant changes which matches every NPI surface may show
      if (req.ignore !== undefined) {
        utils.npi.invalidate()
        utils.pythonApi.getBaselineForecastInfo.invalidate()
      }

      if (forecastChanged) {
        utils.forecasts.getAllForecasts.invalidate()
        utils.forecasts.getRawForecastsForVariants.invalidate()
        utils.forecasts.getVariantForecastForCollections.invalidate()
        utils.product.salesAndForecasts.invalidate()
        // Dates and lifecycle decide demand2's plan period, baselines, insights and stages.
        void utils.demand2.invalidate()
        await utils.variantTableData.variants.invalidate()
        await utils.npi.variants.invalidate()
        utils.productManagement.categorizeVariants.invalidate()
      }

      if (options?.showToast !== false) {
        toast.success(
          `Metadata updated on ${req.skuIds.length} ${req.skuIds.length === 1 ? "SKU" : "SKUs"}`,
        )
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
